import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../lib/api';

export default function WorldBankNew() {
  const nav = useNavigate();
  const [varsByTopic, setVarsByTopic] = useState({});
  const [countries, setCountries] = useState([]);
  const [var1, setVar1] = useState('');
  const [var2, setVar2] = useState('');
  const [country, setCountry] = useState('ZMB');
  const [years, setYears] = useState(40);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [v, c] = await Promise.all([
          api.get('/api/worldbank/variables'),
          api.get('/api/worldbank/countries').catch(() => ({
            countries: [
              { iso3: 'ZAF', name: 'South Africa' },
              { iso3: 'ZMB', name: 'Zambia' },
              { iso3: 'KEN', name: 'Kenya' },
              { iso3: 'NGA', name: 'Nigeria' },
              { iso3: 'USA', name: 'United States' },
              { iso3: 'GBR', name: 'United Kingdom' },
            ],
          })),
        ]);
        setVarsByTopic(v.variables_by_topic);
        setCountries(c.countries);
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'Failed to load options');
      }
    })();
  }, []);

  const varOptions = useMemo(() => {
    const out = [];
    for (const [topic, items] of Object.entries(varsByTopic)) {
      out.push({ group: topic, items });
    }
    return out;
  }, [varsByTopic]);

  const countryName = useMemo(
    () => countries.find((c) => c.iso3 === country)?.name ?? country,
    [countries, country],
  );

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (!var1 || !var2) {
      setErr('Pick both variables.');
      return;
    }
    if (var1 === var2) {
      setErr('Variables must be different.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(
        '/api/worldbank/analyze',
        {
          var1_code: var1,
          var2_code: var2,
          country_iso3: country,
          country_name: countryName,
          years,
        },
      );
      if (res.task_id) {
        nav(`/worldbank/task/${res.task_id}`);
      } else {
        setErr('No task id returned');
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Failed to start analysis');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="hero-gradient py-5">
        <div className="container py-4">
          <div className="text-center text-white">
            <span className="badge bg-info text-dark px-3 py-2 mb-3" style={{ fontSize: '0.82rem' }}>
              <i className="fas fa-flask me-1"></i>Analyze
            </span>
            <h1 style={{ fontWeight: 800, fontSize: '2.4rem' }}>New time-series analysis</h1>
            <p className="lead" style={{ color: 'rgba(255,255,255,0.75)', maxWidth: 600, margin: '0 auto' }}>
              Pick two indicators, a country, and a year window. 10 credits per run.
            </p>
          </div>
        </div>
      </section>

      <div className="main-container">
        <div className="row">
          <div className="col-lg-8 mx-auto">
            <Link to="/worldbank" className="text-muted small">
              <i className="fas fa-arrow-left me-1"></i>Back to World Bank
            </Link>
            <div className="card mt-2">
              <div className="card-body p-4">
                {err && <div className="alert alert-danger">{err}</div>}

                <form onSubmit={submit}>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Variable 1</label>
                    <VarSelect
                      value={var1}
                      onChange={setVar1}
                      options={varOptions}
                      placeholder="Select variable 1…"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Variable 2</label>
                    <VarSelect
                      value={var2}
                      onChange={setVar2}
                      options={varOptions}
                      placeholder="Select variable 2…"
                    />
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-8">
                      <label className="form-label fw-semibold">Country</label>
                      <select
                        className="form-select"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                      >
                        {countries.map((c) => (
                          <option key={c.iso3} value={c.iso3}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label fw-semibold">Years</label>
                      <input
                        type="number"
                        className="form-control"
                        min={10}
                        max={60}
                        value={years}
                        onChange={(e) =>
                          setYears(Math.max(10, Math.min(60, Number(e.target.value))))
                        }
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn btn-cta btn-lg w-100" disabled={busy}>
                    <i className="fas fa-rocket me-1"></i>
                    {busy ? ' Starting…' : ' Analyze (10 credits)'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function VarSelect({ value, onChange, options, placeholder }) {
  return (
    <select
      className="form-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map(({ group, items }) => (
        <optgroup key={group} label={group}>
          {items.map((v) => (
            <option key={v.code} value={v.code}>
              {v.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
